import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { ReportPeriod } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ReportGroupBy, TReportAmountOfRebillsData } from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  exportAmountOfRebills,
  getAmountOfRebills,
} from '@src/services/reports.ts';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload.ts';
import { downloadFile } from '@lib/utils.ts';
import { toast } from 'react-toastify';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportAmountOfRebillsData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.date}
        </p>
        <p className="text-white text-xs">
          Amount of Rebills: {payload[0]?.value}
        </p>
      </div>
    );
  }

  return null;
};

export const AmountOfRebills: FC = () => {
  // Data state
  const [data, setData] = useState<TReportAmountOfRebillsData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Use custom hook for table columns - single metric
  const { columns, tableData } =
    useReportTableColumns<TReportAmountOfRebillsData>({
      data,
      getAccessField: (item) => item.date,
      getMainDataField: (item) => item.amount_of_rebills || 0,
      customColumns: [
        {
          title: 'AMOUNT OF REBILLS, USD',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">${item?.amount_of_rebills ?? 0}</p>
          ),
        },
      ],
      includeTotalsRow: true,
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await getAmountOfRebills(payload);

      setData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await exportAmountOfRebills(payload);

      downloadFile(response.data, 'amount-of-rebills');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Amount of Rebills Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="amount_of_rebills"
                dot={{ fill: '#B6D72C', r: 4 }}
                name="Amount of Rebills"
                stroke="#B6D72C"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download Excel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
